import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useProjectsApi from '../../list/hooks/useProjectsApi'

export default function useProjectEditPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()
	const { getProjectById, updateProject } = useProjectsApi()
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [project, setProject] = useState(null)
	const [form, setForm] = useState({
		nazwa: '',
		status: 'new',
		opis: '',
	})

	const setField = useCallback((key, value) => {
		setForm(prev => ({ ...prev, [key]: value }))
	}, [])

	const handleCancel = useCallback(() => {
		if (id) {
			navigate(`/centrumSzkolen/projekty/details/${id}`)
			return
		}
		navigate('/centrumSzkolen/projekty')
	}, [id, navigate])

	const loadProject = useCallback(async () => {
		setLoading(true)
		const result = await getProjectById(id)
		if (result.error || !result.data) {
			await Swal.fire(
				t('projects.edit.messages.errorTitle'),
				result.error || t('projects.edit.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/projekty')
			return
		}

		const data = result.data
		setProject(data)
		setForm({
			nazwa: data.nazwa || '',
			status: data.status || 'new',
			opis: data.opis || '',
		})
		setLoading(false)
	}, [getProjectById, id, navigate, t])

	useEffect(() => {
		if (id) loadProject()
	}, [id, loadProject])

	const handleSubmit = useCallback(async event => {
		event.preventDefault()
		if (submitting || !project) return

		const nazwa = `${form.nazwa || ''}`.trim()
		if (!nazwa) {
			await Swal.fire(
				t('projects.edit.messages.errorTitle'),
				t('projects.form.messages.nameRequired', { defaultValue: 'Nazwa projektu jest wymagana' }),
				'error'
			)
			return
		}

		setSubmitting(true)
		const result = await updateProject(id, {
			nazwa,
			status: form.status || 'new',
			opis: form.opis || '',
			uwagi: project.uwagi || '',
			steps: project.steps || [],
		})

		if (!result.success) {
			await Swal.fire(
				t('projects.edit.messages.errorTitle'),
				result.error || t('projects.edit.messages.updateFailed'),
				'error'
			)
			setSubmitting(false)
			return
		}

		setSubmitting(false)
		navigate(`/centrumSzkolen/projekty/details/${id}`)
	}, [form, id, navigate, project, submitting, t, updateProject])

	return {
		loading,
		submitting,
		form,
		setField,
		handleCancel,
		handleSubmit,
	}
}
